import React, { useEffect, useMemo, useState } from "react";
import EmptyState from "../../components/Ui/EmptyState";
import { getPublicVideos } from "../../services/video";

const VideoSlides = () => {
  const toEmbedUrl = (url) => {
    try {
      const u = new URL(url);
      const build = (id, qs) => {
        // Use privacy-enhanced mode to avoid cookies
        const base = `https://www.youtube-nocookie.com/embed/${id}`;
        return qs ? `${base}?${qs}` : base;
      };
      if ((u.hostname.includes("youtube.com") || u.hostname.includes("youtube-nocookie.com")) && u.pathname.startsWith("/embed/")) {
        const id = u.pathname.replace("/embed/", "");
        const params = u.searchParams.toString();
        return build(id, params);
      }
      if (u.hostname === "youtu.be") {
        const id = u.pathname.replace(/^\//, "");
        const list = u.searchParams.get("list");
        const start_radio = u.searchParams.get("start_radio");
        const index = u.searchParams.get("index");
        const qs = new URLSearchParams();
        if (list) qs.set("list", list);
        if (start_radio) qs.set("start_radio", start_radio);
        if (index) qs.set("index", index);
        const params = qs.toString();
        return build(id, params);
      }
      if (u.hostname.includes("youtube.com")) {
        const id = u.searchParams.get("v");
        const list = u.searchParams.get("list");
        const start_radio = u.searchParams.get("start_radio");
        const index = u.searchParams.get("index");
        const qs = new URLSearchParams();
        if (list) qs.set("list", list);
        if (start_radio) qs.set("start_radio", start_radio);
        if (index) qs.set("index", index);
        const params = qs.toString();
        if (id) return build(id, params);
      }
    } catch {}
    return url;
  };
  const getYouTubeId = (url) => {
    try {
      const u = new URL(url);
      if (u.hostname === "youtu.be") return u.pathname.replace(/^\//, "");
      if (u.hostname.includes("youtube.com")) return u.searchParams.get("v");
    } catch {}
    return "";
  };
  const isDirectVideoUrl = (url) => {
    try {
      const u = new URL(url);
      const path = (u.pathname || "").toLowerCase();
      return [
        ".mp4",
        ".mov",
        ".webm",
        ".ogg",
        ".ogv",
        ".m4v",
        ".mkv",
      ].some((ext) => path.endsWith(ext));
    } catch {}
    return false;
  };

  const getMimeFromUrl = (url) => {
    try {
      const u = new URL(url);
      const path = (u.pathname || "").toLowerCase();
      if (path.endsWith(".mp4")) return "video/mp4";
      if (path.endsWith(".mov")) return "video/quicktime";
      if (path.endsWith(".webm")) return "video/webm";
      if (path.endsWith(".ogg") || path.endsWith(".ogv")) return "video/ogg";
      if (path.endsWith(".m4v")) return "video/x-m4v";
      if (path.endsWith(".mkv")) return "video/x-matroska";
    } catch {}
    return "video/mp4";
  };
  const [items, setItems] = useState([]);

  const [active, setActive] = useState(null); // { id, url, title }
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [embedSrc, setEmbedSrc] = useState("");
  const [show, setShow] = useState(false);
  const [thumbMap, setThumbMap] = useState({}); // url -> dataURL

  const buildEmbedSrc = (rawUrl) => {
    const base = toEmbedUrl(rawUrl || "");
    if (!base) return "";
    const sep = base.includes("?") ? "&" : "?";
    const cb = Date.now();
    // autoplay + modestbranding + rel=0 + playsinline to reduce tracking/cookies and improve UX
    return `${base}${sep}autoplay=1&modestbranding=1&rel=0&playsinline=1&cb=${cb}`;
  };

  const openModal = (it, idx) => {
    if (!it || !it.url) return;
    // Use a fresh object to avoid accidental mutation and ensure correct url used
    setActive({ id: it._id || it.id, url: it.url, title: it.title || "" });
    setSelectedIndex(typeof idx === 'number' ? idx : items.findIndex(x => (x._id||x.id) === (it._id||it.id)));
    // Reset then set embed to force reload and bypass any cache
    setEmbedSrc("");
    setTimeout(() => {
      setEmbedSrc(buildEmbedSrc(it.url));
      setShow(true);
    }, 0);
  };

  const closeModal = () => {
    setShow(false);
    // Forcefully unload YouTube context to prevent cookie reuse
    setEmbedSrc("about:blank");
    setTimeout(() => setEmbedSrc(""), 0);
    setTimeout(() => setActive(null), 150);
  };

  // Do NOT recompute embedSrc from active directly; we add cache-buster on open

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getPublicVideos();
        const rows = res?.data?.data || [];
        const mapped = rows.map((v, i) => ({ id: v._id || i + 1, _id: v._id, url: v.url, title: v.title || "" }));
        setItems(mapped);
      } catch (e) {
        setItems([]);
      }
    };
    load();
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Generate thumbnails for direct S3 videos (client-side, first frame)
  useEffect(() => {
    const directVideos = items.filter((x) => isDirectVideoUrl(x.url));
    directVideos.forEach((it) => {
      const url = it.url;
      if (!url || thumbMap[url]) return;
      try {
        const video = document.createElement("video");
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.preload = "metadata";
        // Some browsers need a small seek to force decode of first frame
        video.addEventListener("loadeddata", () => {
          try {
            const width = video.videoWidth || 480;
            const height = video.videoHeight || 270;
            const targetW = 480;
            const ratio = width ? targetW / width : 1;
            const w = Math.max(1, Math.round(width * ratio));
            const h = Math.max(1, Math.round(height * ratio));
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, w, h);
            const data = canvas.toDataURL("image/webp", 0.8);
            setThumbMap((m) => ({ ...m, [url]: data }));
          } catch {}
          try { video.src = ""; video.load(); } catch {}
        }, { once: true });
        video.addEventListener("error", () => {
          try { video.src = ""; video.load(); } catch {}
        }, { once: true });
        // Kick off
        video.src = url + (url.includes("?") ? "&" : "?") + "cb=" + Date.now();
      } catch {}
    });
  }, [items, thumbMap]);

  // Also ensure iframe is cleared on page hide/unload
  useEffect(() => {
    const clear = () => {
      try { setEmbedSrc("about:blank"); } catch {}
      try { setEmbedSrc(""); } catch {}
    };
    window.addEventListener("pagehide", clear);
    window.addEventListener("beforeunload", clear);
    return () => {
      window.removeEventListener("pagehide", clear);
      window.removeEventListener("beforeunload", clear);
    };
  }, []);

  // Hide 3rd-party chat widget (Tawk) on Video Library page
  useEffect(() => {
    const api = window.Tawk_API;
    try { api?.hideWidget?.(); } catch {}

    const style = document.createElement("style");
    style.id = "hide-tawk-on-video-slides";
    style.textContent = `
      .tawk-button,
      .tawk-min-chat-icon,
      .tawk-custom-color,
      [class*="tawk-"][role="button"],
      [class*="tawk-"][aria-label*="Chat"],
      [class*="tawk-"][title*="Chat"],
      [class*="tawk-"][data-tawk-id] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    try { document.head.appendChild(style); } catch {}

    return () => {
      try { api?.showWidget?.(); } catch {}
      try {
        const s = document.getElementById("hide-tawk-on-video-slides");
        if (s && s.parentNode) s.parentNode.removeChild(s);
      } catch {}
    };
  }, []);

  return (
    <div className="w-full h-full p-4 overflow-auto">
      <div className="max-w-7xl mx-auto">
        {items.length === 0 && (
          <EmptyState
            icon="play"
            title="We will update soon"
            subtitle="Video resources are coming shortly. Please check back later."
          />
        )}
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          }}
        >
          {items.map((item, i) => (
            <div key={item._id || item.id || i} className="w-full">
              <div
                className="w-full rounded-md overflow-hidden relative bg-black/5 cursor-pointer group"
                style={{ paddingTop: "56.25%" }}
                onClick={() => openModal(item, i)}
                role="button"
                aria-label={`Open ${item.title}`}
              >
                {getYouTubeId(item.url) ? (
                  <img
                    src={`https://img.youtube.com/vi/${getYouTubeId(item.url)}/hqdefault.jpg`}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    draggable={false}
                  />
                ) : isDirectVideoUrl(item.url) ? (
                  thumbMap[item.url] ? (
                    <img
                      src={thumbMap[item.url]}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                    />
                  ) : (
                    <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                      <span className="text-sm">Loading…</span>
                    </div>
                  )
                ) : (
                  <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                    <span className="text-sm">Video</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-white/90 rounded-full p-3 shadow-md">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-gray-900">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-sm text-gray-700 truncate" title={item.title || ""}>{item.title || ""}</div>
              
            </div>
          ))}
        </div>
      </div>

      {active && (
        <div
          className={`fixed inset-0 z-[2147483000] ${show ? "opacity-100" : "opacity-0"} transition-opacity duration-150`}
          aria-modal="true"
          role="dialog"
        >
          <div
            className="absolute inset-0 bg-black/70"
            onClick={closeModal}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className={`w-full max-w-5xl transition-transform duration-150 ${show ? "scale-100" : "scale-95"}`}>
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl">
                <div className="w-full" style={{ paddingTop: "56.25%" }} />
                {getYouTubeId(active.url) ? (
                  <iframe
                    key={embedSrc}
                    title={`active-video-${active.id}`}
                    src={embedSrc}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                ) : (
                  <video
                    key={active.id}
                    className="absolute inset-0 w-full h-full bg-black"
                    controls
                    autoPlay
                    playsInline
                    preload="metadata"
                    crossOrigin="anonymous"
                    onError={() => {
                      try { console.warn("Failed to play video. Check S3 CORS and Content-Type."); } catch {}
                    }}
                  >
                    <source src={active.url} type={getMimeFromUrl(active.url)} />
                  </video>
                )}
              </div>
              <div className="mt-3 text-center text-white/90 text-sm">{active.title || ""}</div>
              
              <button
                onClick={closeModal}
                className="absolute top-3 right-4 md:top-6 md:right-8 p-2 rounded-full bg-white/90 hover:bg-white text-gray-700"
                aria-label="Close video"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoSlides;


